import { type ProjectCode, type CountryCode } from '@constants'
import { type ValueOf } from '@dni-types/utilities/value-of'
import { useCallback, useState } from 'react'
import { I18nextProvider } from 'react-i18next'
import { cosignConfigMap } from '../../../../constants/cosign-config'
import cosignInstance from '../../../../i18n/i18nConfig'
import { resetCosignCookies } from '../../../../utils/cookie'
import { CosignContext } from '../../../useContextCosign'
import { CardLogin } from '../../CardLogin'
import { loginStepGroupBuying } from '../types'
import { LoginEmailGroupBuying } from './LoginEmailGroupBuying'
import { LoginContextGroupBuying, type LoginGroupBuyingContextProps } from './useContextLoginGroupBuying'
import { ValidationEmailGroupBuying } from './ValidationEmailGroupBuying'

interface LoginPortalGroupBuyingProps {
  backLoginUrl: LoginGroupBuyingContextProps['backLoginUrl']
  countryCode: CountryCode
  fallbackUrl: LoginGroupBuyingContextProps['fallbackUrl']
  redirectUrl: LoginGroupBuyingContextProps['redirectUrl']
  tokenLoginUrl: LoginGroupBuyingContextProps['tokenLoginUrl']
  projectCode: ProjectCode
  emailDomain: LoginGroupBuyingContextProps['emailDomain']
}

export const LoginPortalGroupBuying = (props: LoginPortalGroupBuyingProps) => {
  const { backLoginUrl, countryCode, fallbackUrl, redirectUrl, tokenLoginUrl, projectCode, emailDomain } = props

  const [email, setEmail] = useState('')
  const [prevStepStack, setPrevStepStack] = useState<ValueOf<typeof loginStepGroupBuying>[]>([loginStepGroupBuying.LOGIN_EMAIL])
  const [step, _setStep] = useState<ValueOf<typeof loginStepGroupBuying>>(loginStepGroupBuying.LOGIN_EMAIL)
  const setStep = useCallback((nextStep: ValueOf<typeof loginStepGroupBuying>) => {
    if (nextStep === loginStepGroupBuying.LOGIN_EMAIL) {
      setPrevStepStack([loginStepGroupBuying.LOGIN_EMAIL])
      _setStep(loginStepGroupBuying.LOGIN_EMAIL)
      setEmail('')
      resetCosignCookies()
    }
    else {
      setPrevStepStack([...prevStepStack, step])
      _setStep(nextStep)
    }
  }, [prevStepStack, step])

  const backStep = () => {
    const backStep = prevStepStack.at(-1)
    setStep(backStep || loginStepGroupBuying.LOGIN_EMAIL)
    setPrevStepStack(prevStepStack.slice(0, -1))
  }

  const loginContextValue: LoginGroupBuyingContextProps = {
    setStep,
    email,
    setEmail,
    backStep,
    backLoginUrl,
    fallbackUrl,
    redirectUrl,
    tokenLoginUrl,
    countryCode,
    emailDomain,
  }

  return (
    <I18nextProvider i18n={cosignInstance}>
      <CardLogin>
        <CosignContext.Provider value={{
          ...cosignConfigMap[projectCode as Exclude<ProjectCode, 'FP' | 'HN' | 'ST'>],
          title: 'Buygather',
          environment: 'campaign',
        }}
        >
          <LoginContextGroupBuying.Provider value={loginContextValue}>
            {step === loginStepGroupBuying.LOGIN_EMAIL && <LoginEmailGroupBuying />}
            {step === loginStepGroupBuying.VALIDATE_EMAIL && <ValidationEmailGroupBuying />}
          </LoginContextGroupBuying.Provider>
        </CosignContext.Provider>
      </CardLogin>
    </I18nextProvider>
  )
}
